import { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import apiClient from '@/utils/api';

const copyToClipboard = (text) => {
  try {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  } catch {
    toast.error('Copy failed');
  }
};

const sampleCurl = (url, secret) => `curl -X POST '${url}' \\
  -H 'Content-Type: application/json' \\
  -H 'X-CRM-Secret: ${secret}' \\
  -d '{
    "full_name": "Priya Sharma",
    "phone": "+919876543210",
    "email": "priya@example.com",
    "desired_course": "MBBS",
    "desired_country": "Georgia",
    "source": "website",
    "form_name": "Contact Form - MBBS",
    "page_url": "https://patrickseducare.in/mbbs-georgia",
    "notes": "Asked about fees",
    "utm_source": "google",
    "utm_medium": "cpc",
    "utm_campaign": "mbbs-feb-2026"
  }'`;

const sampleJs = (url, secret) => `// Drop into any onSubmit handler on your website / landing page
async function submitLeadToCRM(formData) {
  const res = await fetch('${url}', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CRM-Secret': '${secret}',  // ⚠️ NEVER expose this in client-side JS
                                          // call this from your backend / a server route
    },
    body: JSON.stringify({
      full_name: formData.name,
      phone: formData.phone,
      email: formData.email,
      desired_course: formData.course,
      desired_country: formData.country,
      source: 'website',
      form_name: 'Contact Form',
      page_url: window.location.href,
      utm_source: new URLSearchParams(location.search).get('utm_source'),
      utm_medium: new URLSearchParams(location.search).get('utm_medium'),
      utm_campaign: new URLSearchParams(location.search).get('utm_campaign')
    })
  });
  return res.json();
}`;

export default function WebsiteFormBridgeSettings({ inline = false } = {}) {
  const [loading, setLoading] = useState(true);
  const [cfg, setCfg] = useState(null);
  const [tab, setTab] = useState('curl');

  useEffect(() => {
    (async () => {
      try {
        const r = await apiClient.get('/website-leads/config');
        setCfg(r.data);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to load website form config');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const snippet = !cfg ? '' : tab === 'curl' ? sampleCurl(cfg.webhook_url, cfg.secret) : sampleJs(cfg.webhook_url, cfg.secret);

  const body = (
    <>
      {loading ? (
          <div className="text-sm text-slate-500">Loading…</div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-500">Webhook URL</Label>
                <div className="flex gap-2 mt-1">
                  <Input value={cfg?.webhook_url || ''} readOnly data-testid="website-webhook-url" />
                  <Button variant="outline" onClick={() => copyToClipboard(cfg?.webhook_url || '')} data-testid="website-copy-url-btn">Copy</Button>
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-500">Shared Secret (X-CRM-Secret header)</Label>
                <div className="flex gap-2 mt-1">
                  <Input type="password" value={cfg?.secret || ''} readOnly data-testid="website-secret" />
                  <Button variant="outline" onClick={() => copyToClipboard(cfg?.secret || '')} data-testid="website-copy-secret-btn">Copy</Button>
                </div>
                <p className="text-xs text-red-600 mt-1">⚠️ NEVER expose this in client-side JS — call from your backend only</p>
              </div>
            </div>

            <div className="border rounded-lg p-3 bg-slate-50 text-xs space-y-1.5">
              <strong className="text-sm">Required fields:</strong>
              <ul className="list-disc ml-5">
                <li><code className="bg-white px-1 rounded border">full_name</code> · string</li>
                <li><code className="bg-white px-1 rounded border">phone</code> · any format ( <code>9876543210</code>, <code>+919876543210</code>, <code>(91)98765-43210</code> — all normalised )</li>
              </ul>
              <strong className="text-sm block pt-2">Optional fields:</strong>
              <p className="text-slate-600">
                <code>email</code> · <code>desired_course</code> · <code>desired_country</code> · <code>city</code> · <code>state</code> · <code>source</code> · <code>form_name</code> · <code>page_url</code> · <code>notes</code> · <code>utm_source</code> · <code>utm_medium</code> · <code>utm_campaign</code> · <code>utm_content</code> · <code>utm_term</code> · <code>external_id</code>
              </p>
              <p className="text-slate-600 pt-1.5">
                <strong>Dedup:</strong> phone match against WATI / Meta / manual / older website leads → no duplicate lead created (existing one updated + counselor notified).
              </p>
              <div className="pt-2 mt-2 border-t border-slate-200">
                <strong className="text-sm block">Gradbud lead form (gradbud.in/leadform):</strong>
                <p className="text-slate-600">
                  The form's own field names are accepted directly — <code>full_name</code>, <code>phone_number</code>, <code>city</code>, <code>program_interest</code> (→ course), <code>highest_qualification</code>, <code>percentage_grade</code>. Send <code>"source": "gradbud_website"</code> to tag these leads.
                </p>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex gap-2">
                  <button onClick={() => setTab('curl')} className={`text-xs px-3 py-1 rounded ${tab === 'curl' ? 'bg-indigo-600 text-white' : 'bg-slate-200 text-slate-700'}`} data-testid="website-tab-curl">cURL</button>
                  <button onClick={() => setTab('js')} className={`text-xs px-3 py-1 rounded ${tab === 'js' ? 'bg-indigo-600 text-white' : 'bg-slate-200 text-slate-700'}`} data-testid="website-tab-js">JavaScript</button>
                </div>
                <Button size="sm" variant="outline" onClick={() => copyToClipboard(snippet)} data-testid="website-copy-snippet-btn">
                  <i className="fas fa-copy mr-1.5"></i>Copy snippet
                </Button>
              </div>
              <pre className="bg-slate-900 text-slate-100 text-[11px] leading-snug p-3 rounded-lg overflow-auto max-h-80" data-testid="website-snippet-preview">
                {snippet}
              </pre>
            </div>

            <div className="text-xs text-slate-500 bg-amber-50 border border-amber-200 rounded-lg p-3">
              <strong>Security best practice:</strong> Do <strong>NOT</strong> embed the secret in front-end / browser JS — anyone viewing the page source could steal it. Always proxy via your own backend or a serverless function. The browser submits the form to <em>your</em> server → your server posts to CRM with the secret server-side.
            </div>
          </>
        )}
    </>
  );

  if (inline) {
    return <div data-testid="website-form-bridge-card" className="space-y-5">{body}</div>;
  }

  return (
    <Card data-testid="website-form-bridge-card" className="border-l-4 border-l-indigo-500">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
              <i className="fas fa-globe text-indigo-600 text-xl"></i>
            </div>
            <div>
              <CardTitle>Website Form → CRM</CardTitle>
              <CardDescription>
                Push leads from your website contact / landing-page forms directly into the CRM.
              </CardDescription>
            </div>
          </div>
          <Badge
            variant="outline"
            className={cfg?.configured ? 'bg-green-100 text-green-800 border-green-300' : 'bg-yellow-100 text-yellow-800 border-yellow-300'}
            data-testid="website-bridge-status"
          >
            {cfg?.configured ? 'Ready' : 'Not Configured'}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-5">{body}</CardContent>
    </Card>
  );
}
